import type { Field, FieldADTValue, FieldCellValue, HighLightConditions } from '@lighthouse/core'
import {
    editableInCellFieldTypes,
    getIsAppointField,
    isMobileDevice,
    NO_EDIT_SYSTEM_FIELD,
    notEditableFieldTypes,
    ViewFieldValuePreviewer
} from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import type { AdvancedTableContentState, AdvancedTableTooltipState } from './AdvancedTableContent'

interface TableCellProps {
    blockId: string
    recordId: string
    field: Field
    data?: FieldCellValue
    width?: number
    styleConditions?: HighLightConditions
    style?: React.CSSProperties
    hoverCellColor: string
    primaryDataSourceFieldIds?: Set<string>
    onOpenEditor?: (data: AdvancedTableContentState) => void
    onOpenTooltip?: (data: AdvancedTableTooltipState) => void
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
}

const SCxTableCell = styled.div<{ hoverCellColor: string }>`
    position: relative;
    min-height: 40px;
    flex-shrink: 0;

    border-right: 1px solid var(--color-gray-200);

    &:hover {
        background: var(--color-white);
        &:before {
            content: '';
            position: absolute;
            width: calc(100%);
            height: calc(100%);
            left: 0px;
            top: 0;
            border: ${({ hoverCellColor }) => `2px solid ${hoverCellColor}`};
            background-color: transparent;
            /* z-index: 1; */
        }
    }

    &.cell-active {
        z-index: 1;
        background-color: var(--color-white);
        &:before {
            content: '';
            position: absolute;
            width: calc(100%);
            height: calc(100%);
            left: 0px;
            top: 0;
            border: 2px solid var(--color-app-main);
            background-color: transparent;
            /* z-index: 10; */
        }
    }
`

const SCxTableCellPopperAnchor = styled.div`
    position: absolute;
    left: -2px;
    top: -2px;
    height: 40px;
    z-index: 2;
    overflow: visible;
`

const SCxTableCellContent = styled.div`
    position: relative;
    width: 100%;
    height: 40px;
    font-size: var(--font-size-normal);
`

export const TableAdvancedValue: React.FC<TableCellProps> = memo(
    ({
        blockId,
        recordId,
        field,
        data,
        width,
        styleConditions,
        style,
        hoverCellColor,
        primaryDataSourceFieldIds,
        onOpenEditor,
        onOpenTooltip,
        onCellChange
    }) => {
        const { id: fieldId } = field
        const editorRef = useRef<HTMLDivElement>(null)
        // 使用缓存的设备类型判断（基于 UserAgent，用于触摸交互）
        const isMobile = isMobileDevice()
        const isNoneShape = notEditableFieldTypes.has(field.type)
        const isNoEditSystemField = getIsAppointField(fieldId, NO_EDIT_SYSTEM_FIELD)
        const isEditorInCell = editableInCellFieldTypes.has(field.type)
        const popperAnchorRef = useRef<HTMLDivElement>(null)
        const dataKey = useMemo(() => `${blockId}&${recordId}&${fieldId}`, [blockId, fieldId, recordId])

        const isEnableField = useMemo(() => primaryDataSourceFieldIds?.has(fieldId), [fieldId, primaryDataSourceFieldIds])

        const handleCellChange = useCallback(
            (value: FieldADTValue) => {
                if (!onCellChange) {
                    return Promise.resolve(false)
                }
                return onCellChange(recordId, value)
            },
            [onCellChange, recordId]
        )

        const handleOpen = useCallback(() => {
            const cellElement = editorRef.current
            if (!cellElement?.className.includes('active')) {
                return
            }

            // 复选框在单元格内编辑
            if (isEditorInCell) {
                return
            }

            if (!isEnableField) {
                onOpenTooltip?.({
                    toolTipAnchorElement: cellElement,
                    message: '该副表字段不允许编辑单元格内容'
                })
                return
            }

            if (isNoneShape || isNoEditSystemField) {
                // setOpened(true)
                onOpenTooltip?.({
                    toolTipAnchorElement: cellElement,
                    message: '该字段类型不允许编辑单元格内容'
                })
                return
            }
            if (popperAnchorRef.current && cellElement) {
                onOpenEditor?.({
                    popperAnchorElement: popperAnchorRef.current,
                    currentRecordId: recordId,
                    currentWidth: width,
                    currentField: field
                })
            }
        }, [field, isEditorInCell, isEnableField, isNoEditSystemField, isNoneShape, onOpenEditor, onOpenTooltip, recordId, width])

        const handlePointerDown = useCallback(() => {
            if (isMobile) {
                return
            }
            handleOpen()
        }, [handleOpen, isMobile])

        const handleTouchStart = useCallback(() => {
            if (!isMobile) {
                return
            }
            handleOpen()
        }, [handleOpen, isMobile])

        return (
            <SCxTableCell
                style={style}
                onPointerDown={handlePointerDown}
                onTouchEnd={handleTouchStart}
                // onTouchStart={handleTouchStart}
                ref={editorRef}
                data-key={dataKey}
                hoverCellColor={hoverCellColor}
            >
                <SCxTableCellContent>
                    <ViewFieldValuePreviewer
                        field={field}
                        data={data}
                        isWrap={false}
                        width={width}
                        disabled={!isEnableField}
                        enableEdit
                        styleConditions={styleConditions}
                        onCellChange={handleCellChange}
                    />
                </SCxTableCellContent>
                <SCxTableCellPopperAnchor data-type="anchor" data-id={fieldId} ref={popperAnchorRef} />
            </SCxTableCell>
        )
    },
    isDeepEqual
)
